import { P2GTab, P2GTabs } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RoutingConstantsLabels } from '~/apps/smartsend/lib/consts/RoutingConstants';
import useGoto from '~/hooks/use-goto';
import useSmartNav from '~/hooks/use-smart-nav';
import { TranslationNamespaces } from '~/lib/constants';

const sanitisedRoute = (route: string) =>
  route
    .replace(/\/:.*$/, '')
    .replace(/[-/]/g, '')
    .toUpperCase();

// Generic underline tab bar (the "SmartSend Orders" style). Optionally renders a
// per-route count badge in the label, e.g. "Orders InTransit (0)" - pass `counts`
// keyed by route. Used across the account-area tabbed pages (My Orders, Account,
// Support). The SmartSend orders page keeps its own SmartSendNavigation.
const Navvy = ({
  routes,
  prefix,
  counts,
  labels
}: {
  routes: string[];
  prefix: string;
  counts?: Record<string, number>;
  labels?: Record<string, string>;
}) => {
  const { currentPageRoute } = useSmartNav();
  const { t } = useTranslation(TranslationNamespaces.MYACCOUNT);
  const { goTo } = useGoto();

  const links = useMemo(
    () =>
      routes.map((r) => {
        const base =
          labels?.[r] ?? t(`NAV_${prefix}_${sanitisedRoute(r)}_LINK`.toUpperCase(), RoutingConstantsLabels[r] || r);
        const count = counts?.[r];
        return { route: r, label: count === undefined ? base : `${base} (${count})` };
      }),
    [t, routes, prefix, counts, labels]
  );

  const currentIndex = useMemo(() => {
    const index = links.findIndex((link) => link.route === currentPageRoute);
    return index >= 0 ? index : 0;
  }, [links, currentPageRoute]);

  if (links.length <= 1) {
    return null;
  }

  return (
    <P2GTabs value={currentIndex}>
      {links.map((link, index) => (
        <P2GTab key={link.route} label={link.label} onClick={() => goTo(link.route)} index={index} />
      ))}
    </P2GTabs>
  );
};

export default Navvy;
